/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag Fragment
 * @graphql cc
 */
import type { FC, PropsWithChildren } from 'react';
// eslint-disable-next-line no-unused-vars -- Fragment is used in the code
import { useCallback, useEffect, useRef, Fragment } from 'react';
// We have deprecated unstated. Please use react-sweet-state instead
// eslint-disable-next-line no-restricted-imports
import { Subscribe } from 'unstated';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from '@apollo/react-hooks';
import { useIntl } from 'react-intl-next';
import { cssMap } from '@compiled/react';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import type { BoxProps } from '@atlaskit/primitives';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Flex, xcss } from '@atlaskit/primitives';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/experiments';
// eslint-disable-next-line no-unused-vars -- jsx is required for the code in this file
import { jsx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import { ExitingPersistence } from '@atlaskit/motion';

import { CoverPictureQuery } from '@confluence/page-cover-picture/entry-points/CoverPictureQuery.graphql.ts';
import { CoverPictureIdPropertyKey } from '@confluence/page-cover-picture/entry-points/pageCoverPictureTypes.ts';
import { propertyToId } from '@confluence/page-cover-picture/entry-points/coverPictureHelperFunctions.ts';
import { getUnsplashPhotos } from '@confluence/page-cover-picture/entry-points/unsplashAPI.ts';
import type {
	CoverPictureQueryType,
	CoverPictureQueryVariables,
} from '@confluence/page-cover-picture/entry-points/CoverPictureQuery.ts';
import { useApprovals } from '@confluence/approvals/entry-points/ApprovalsContext.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { EmojiTitleStateContainer } from '@confluence/emoji-title/entry-points/emojiTitleStateContainer.ts';
import { ContentStateStateContainer } from '@confluence/content-state/entry-points/ContentStateStateContainer.ts';
import { useEmbeddedViewPageFeatures } from '@confluence/embedded-confluence-allowed-features/entry-points/view-page.ts';
import { useIsEmbeddedConfluence_DO_NOT_USE } from '@confluence/embedded-confluence-check';
import { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { useContentAppearance } from '@confluence/content-appearance/entry-points/ContentAppearanceContext.ts';
import {
	EmojiTitleQueryArtifact,
	type EmojiTitleQuery$data,
	type EmojiTitleQuery$variables,
} from '@confluence/emoji-title/entry-points/EmojiTitleQuery.ts';
import { getDraftShareIdFromQueryParam } from '@confluence/route-manager-utils/entry-points/getDraftShareIdFromQueryParam.ts';
import {
	getHasEmoji,
	getHasLiveDocsDefaultEmoji,
} from '@confluence/emoji-title/entry-points/transformer.ts';
import { useEmojiStatusFieldName } from '@confluence/emoji-title/entry-points/useEmojiStatusFieldName.ts';
import { useSSRPlaceholderReplaceIdProp } from '@confluence/loadable';
import { confluenceLocalStorageInstance as localStorage } from '@confluence/storage-manager/entry-points/storageInstance.ts';
import { keys as localStorageKeys } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { useSpaceHomepageId } from '@confluence/space-utils/entry-points/useSpaceHomepageId.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { SuggestWhiteboardOnPageTitleExperiment } from '@confluence/experiment-suggest-whiteboard-based-on-page-title/entry-points/SuggestWhiteboardOnPageTitleExperiment.ts';

import { getAIUnsplashPrompt } from './AISuggestedImageHeaderAPI.ts';
import { TitleSection } from './TitleSection.tsx';
// eslint-disable-next-line import/no-cycle
import { ToolbarAboveTitle } from './ToolbarAboveTitle.tsx';
import type { EditorTitleWithTitleAreaComponentsProps } from './titleareaContentComponentsTypes.ts';
import { AlignmentButtonContainer } from './AlignmentButtonContainer.tsx';

export const TOOLBAR_OPACITY_VAR: any = '--toolbar-opacity';

const editorTitleWrapperStyles = cssMap({
	root: {
		width: '100%',
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start',
		[TOOLBAR_OPACITY_VAR]: 0,
		'&:hover': {
			[TOOLBAR_OPACITY_VAR]: 1,
		},
	},
	paddingTopSpace300: {
		paddingTop: token('space.300'),
	},
	paddingTopSpace0: {
		paddingTop: token('space.0'),
	},
	topSpacing: {
		marginTop: token('space.negative.400'),
		paddingTop: token('space.200'),
	},
	paddingBottomSpace300: {
		paddingBottom: token('space.300'),
	},
	newEditorTitleHoverTopPadding: {
		paddingTop: token('space.250'),
	},
});

const titleBoxStylesAlignSelfCenter = xcss({
	alignSelf: 'center',
});

const authorBylinePositionRelative = xcss({
	position: 'relative',
});

const editorTitleBaseStyles = xcss({
	width: '100%',
	marginTop: 'space.negative.200',
	marginBottom: 'space.400',
});

const editorTitleSmallerMarginBottomBaseStyles = xcss({
	marginBottom: 'space.300',
});

type EditorTitleWithButtonsWrapperProps = {
	viewMode: boolean;
	isNewEditorBylineLayout: boolean;
	isContentTopperComponentPresent: boolean;
	hasCoverPicture: boolean;
} & BoxProps<'div'>;

const EditorTitleWithButtonsWrapper: FC<PropsWithChildren<EditorTitleWithButtonsWrapperProps>> = ({
	viewMode,
	isContentTopperComponentPresent,
	isNewEditorBylineLayout,
	hasCoverPicture,
	children,
}) => {
	const ssrPlaceholderIdProp = useSSRPlaceholderReplaceIdProp();

	return (
		<div
			// TODO: Cleanup CSS when cleaning up FG PGXT-8139
			css={[
				editorTitleWrapperStyles.root,
				viewMode
					? editorTitleWrapperStyles.paddingTopSpace300
					: editorTitleWrapperStyles.paddingTopSpace0,
				isContentTopperComponentPresent && editorTitleWrapperStyles.topSpacing,
				isContentTopperComponentPresent &&
					isNewEditorBylineLayout &&
					editorTitleWrapperStyles.paddingBottomSpace300,
				isContentTopperComponentPresent &&
					!hasCoverPicture &&
					editorTitleWrapperStyles.newEditorTitleHoverTopPadding,
			]}
			data-testid="editor-title-with-buttons-div"
			{...ssrPlaceholderIdProp}
		>
			{children}
		</div>
	);
};
EditorTitleWithButtonsWrapper.displayName = 'EditorTitleWithButtonsWrapper';

export const EditorTitleWithTitleAreaComponents = ({
	contentId,
	isPublishingContent,
	contentType,
	userOffline,
	isTitleDisabled,
	linkCreation,
	isUnpublishedDraft,
	children: editorTitle,
	isTemplateEditor = false,
	isTitleCenterAligned,
	viewMode,
	contentTopperComponent,
	contentClassificationMetadata,
	hasClassification,
	spaceKey,
	isEditorEmpty,
	isTitleAlignmentLoading,
	contentComponentsDisabled,
	isStatusDisconnected,
	coverPictureState,
	title,
	titleAfterBlur,
	isLivePage,
	coverImageWidthCached,
	hideSuggestedTitle = false,
	hideContentState = false,
	hideEditorAppearanceButton = false,
	hideContentModeButton = false,
	hideAlignmentButton = false,
	pageTitleContent,
	aiHeaderImageStatus,
	aiHeaderImageFileId,
	generateAIHeaderImage,
	setAIHeaderImageStatus,
	autoPageEmojiCohort,
	emojiTitleDiscovered,
	version,
}: EditorTitleWithTitleAreaComponentsProps) => {
	const { contentAppearance } = useContentAppearance();
	const isPageContentFullWidth = contentAppearance.appearance === 'full-width';
	const emojiStatusFieldName = useEmojiStatusFieldName();
	const { locale } = useIntl();
	const prevTitleStateRef = useRef<string>();
	const homepageId = useSpaceHomepageId(spaceKey);
	const isSpaceOverview = homepageId === contentId;
	const { hideTitleToolbar } = useEmbeddedViewPageFeatures();
	const isEmbeddedConfluence = useIsEmbeddedConfluence_DO_NOT_USE();
	const showTitleToolbar = isEmbeddedConfluence ? !hideTitleToolbar : true;

	const { approvalRequest, availableWorkflowStatuses } = useApprovals();
	const hasActiveWorkflowStatus =
		!!approvalRequest?.application?.workflowStatusId &&
		availableWorkflowStatuses.some((s) => s.id === approvalRequest.application.workflowStatusId) &&
		expValEquals('confluence_approvals_eap', 'isEnabled', true);

	const initialTitleRef = useRef<string | null | undefined>(title);

	const { data } = useQuery<CoverPictureQueryType, CoverPictureQueryVariables>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Read https://go/connie-relay-migration-fyi
		CoverPictureQuery,
		{
			fetchPolicy: window.__SSR_RENDERED__ ? 'cache-first' : 'cache-and-network',
			context: { single: true },
			variables: {
				contentId,
				status: ['current', 'draft'],
				draftShareId: getDraftShareIdFromQueryParam(),
			},
		},
	);

	const { data: emojiData, error: emojiError } = useQueryInterop<
		EmojiTitleQuery$data,
		EmojiTitleQuery$variables
	>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Read https://go/connie-relay-migration-fyi
		EmojiTitleQueryArtifact,
		{
			variables: {
				contentId,
				status: ['current', 'draft'],
				skipPublishedFragment: false,
				draftShareId: getDraftShareIdFromQueryParam(),
			},
		},
	);

	const hasDefaultTitleEmoji = isLivePage && getHasLiveDocsDefaultEmoji(emojiData);
	const hasEmoji = !!(
		emojiData &&
		(getHasEmoji(emojiData, emojiStatusFieldName) || hasDefaultTitleEmoji)
	);

	const properties = data?.content?.nodes?.[0]?.properties?.nodes || [];
	// we only need the draft info since this will just be used to determine what component to show during edit mode
	const draft = properties.find((p) => p?.key === CoverPictureIdPropertyKey.DRAFT);
	const coverPictureInfo = propertyToId(draft);
	const hasCoverPicture = !!coverPictureInfo?.value?.id;
	const isUnsplash = coverPictureInfo?.value?.id.includes('images.unsplash.com');
	const getUnsplashImage = useCallback(async () => {
		if (editorExperiment('platform_editor_ai_unsplash_page_header', true) !== true) {
			return;
		}

		if (!isUnpublishedDraft && !isLivePage) {
			return;
		}

		if (localStorage.getItem(localStorageKeys.AI_UNSPLASH_HEADER_ACCEPTED) === 'false') {
			return;
		}

		if (
			!titleAfterBlur ||
			coverPictureState.state.hasCoverPicture ||
			coverPictureState.state.isUploadingPicture
		) {
			return;
		}

		const currentTitleState = titleAfterBlur;

		if (prevTitleStateRef.current === currentTitleState) {
			return;
		}

		void coverPictureState.setIsUploadingPicture(true);
		void coverPictureState.setIsAIUnsplashImage(true);

		const imageSearchString = await getAIUnsplashPrompt({
			title: titleAfterBlur,
			locale,
		});

		const unsplashImages = await getUnsplashPhotos(imageSearchString, 0, true);

		if (unsplashImages?.length > 0) {
			void coverPictureState.setAISuggestedUnsplashImages(unsplashImages);
		}

		prevTitleStateRef.current = currentTitleState;
	}, [titleAfterBlur, locale, coverPictureState, isUnpublishedDraft, isLivePage]);

	useEffect(() => {
		void getUnsplashImage();
	}, [titleAfterBlur, getUnsplashImage]);

	useEffect(() => {
		void coverPictureState.setHasCoverPicture(hasCoverPicture);
	}, [hasCoverPicture, coverPictureState]);

	const shouldRenderAboveTitle = !isTemplateEditor && showTitleToolbar;
	const isNewEditorBylineLayout =
		isLivePage || (!isSpaceOverview && fg('cc_page_experiences_new_editor_byline'));

	return (
		<>
			{emojiError ? <ErrorDisplay error={emojiError} /> : null}
			<Subscribe
				to={[
					EmojiTitleStateContainer,
					ContentStateStateContainer,
					AlignmentButtonContainer,
					FlagsStateContainer,
				]}
			>
				{(
					emojiTitleState: EmojiTitleStateContainer,
					contentStateState: ContentStateStateContainer,
					alignmentButtonState: AlignmentButtonContainer,
					flags: FlagsStateContainer,
				) => (
					<Flex
						direction="column"
						xcss={[
							editorTitleBaseStyles,
							isNewEditorBylineLayout && editorTitleSmallerMarginBottomBaseStyles,
						]}
					>
						<EditorTitleWithButtonsWrapper
							viewMode={!!viewMode}
							isContentTopperComponentPresent={!!contentTopperComponent}
							isNewEditorBylineLayout={isNewEditorBylineLayout}
							hasCoverPicture={hasCoverPicture}
						>
							{shouldRenderAboveTitle ? (
								<ToolbarAboveTitle
									contentId={contentId}
									userOffline={userOffline}
									isUnpublishedDraft={isUnpublishedDraft}
									isTitleDisabled={isTitleDisabled}
									isAddHeaderImageButtonFocused={
										coverPictureState.state.isAddHeaderImageButtonFocused
									}
									isCoverPictureModalOpen={coverPictureState.state.isModalOpen}
									hasCoverPicture={hasCoverPicture}
									isUploadingPicture={coverPictureState.state.isUploadingPicture}
									setSelectedEmoji={emojiTitleState.setSelectedEmoji}
									onEmojiPickerLoaded={emojiTitleState.onEmojiPickerLoaded}
									isEmojiButtonFocused={emojiTitleState.state.isEmojiButtonFocused}
									onUpdateStartEmoji={emojiTitleState.onUpdateStart}
									onUpdateEndEmoji={emojiTitleState.onUpdateEnd}
									contentType={contentType}
									isPublishingContent={isPublishingContent}
									contentStateState={contentStateState}
									hasContentState={
										!!contentStateState.state.contentState || hasActiveWorkflowStatus
									}
									isSpaceOverview={isSpaceOverview}
									isTitleCenterAligned={isTitleCenterAligned}
									alignmentButtonState={alignmentButtonState}
									coverPictureState={coverPictureState}
									// need to pass in the state value directly or the component will not re-render
									coverPictureWidth={coverPictureState.state.coverPictureWidth}
									isSecondEmojiPickerOpen={emojiTitleState.state.openSecondEmojiPicker}
									flags={flags}
									contentClassificationMetadata={contentClassificationMetadata}
									hasClassification={hasClassification}
									isReadOnly={viewMode}
									spaceKey={spaceKey}
									isEditorEmpty={isEditorEmpty}
									contentComponentsDisabled={contentComponentsDisabled}
									isStatusDisconnected={isStatusDisconnected}
									isAIUnsplashImage={coverPictureState.state.isAIUnsplashImage}
									aiSuggestedUnsplashImages={coverPictureState.state.aiSuggestedUnsplashImages}
									hasEmoji={hasEmoji}
									coverImageWidthCached={coverImageWidthCached}
									isUnsplash={isUnsplash}
									hideSuggestedTitle={hideSuggestedTitle}
									hideContentState={hideContentState}
									hideEditorAppearanceButton={hideEditorAppearanceButton}
									hideContentModeButton={hideContentModeButton}
									hideAlignmentButton={hideAlignmentButton}
									aiHeaderImageStatus={aiHeaderImageStatus}
									aiHeaderImageFileId={aiHeaderImageFileId}
									generateAIHeaderImage={generateAIHeaderImage}
									setAIHeaderImageStatus={setAIHeaderImageStatus}
									autoPageEmojiCohort={autoPageEmojiCohort}
									emojiTitleDiscovered={emojiTitleDiscovered}
								/>
							) : null}
							<TitleSection
								editorTitle={editorTitle}
								contentTopperComponent={contentTopperComponent}
								contentId={contentId}
								isTemplateEditor={isTemplateEditor}
								onUpdateStartEmoji={emojiTitleState.onUpdateStart}
								onUpdateEndEmoji={emojiTitleState.onUpdateEnd}
								isTitleDisabled={isTitleDisabled}
								isTitleCenterAligned={isTitleCenterAligned}
								linkCreation={linkCreation}
								hasCoverPicture={hasCoverPicture}
								hasEmoji={hasEmoji}
								coverPictureWidth={coverPictureState.state.coverPictureWidth}
								isRemovingEmoji={emojiTitleState.state.isRemovingEmoji}
								isReadOnly={viewMode}
								contentClassificationMetadata={contentClassificationMetadata}
								isPageContentFullWidth={isPageContentFullWidth}
								hasClassification={hasClassification}
								isTitleAlignmentLoading={isTitleAlignmentLoading}
								isSpaceOverview={isSpaceOverview}
								pageTitleContent={pageTitleContent} // Prop for contextualized emoji
								autoPageEmojiCohort={autoPageEmojiCohort}
								isUnpublishedDraft={isUnpublishedDraft}
								isEditorEmpty={isEditorEmpty}
								emojiTitleDiscovered={emojiTitleDiscovered}
								contentType={contentType}
							/>
						</EditorTitleWithButtonsWrapper>

						{fg('cc_fd_suggest_whiteboards_on_page_title_gate') ? (
							<Box xcss={authorBylinePositionRelative}>
								{isNewEditorBylineLayout && contentTopperComponent && (
									<Box
										testId="content-topper-wrapper"
										xcss={[isTitleCenterAligned && titleBoxStylesAlignSelfCenter]}
									>
										{contentTopperComponent}
									</Box>
								)}
								<ExitingPersistence appear>
									<SuggestWhiteboardOnPageTitleExperiment
										spaceKey={spaceKey}
										contentId={contentId}
										title={title}
										initialTitle={initialTitleRef.current}
										titleAfterBlur={titleAfterBlur}
										isLivePage={isLivePage}
										isUnpublishedDraft={isUnpublishedDraft}
										version={version}
									/>
								</ExitingPersistence>
							</Box>
						) : (
							isNewEditorBylineLayout &&
							contentTopperComponent && (
								<Box
									testId="content-topper-wrapper"
									xcss={[isTitleCenterAligned && titleBoxStylesAlignSelfCenter]}
								>
									{contentTopperComponent}
								</Box>
							)
						)}
					</Flex>
				)}
			</Subscribe>
		</>
	);
};
EditorTitleWithTitleAreaComponents.displayName = 'EditorTitleWithTitleAreaComponents';
